import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { getUser } from "../lib/auth";
import { toast } from "sonner";
import { Mail, Sparkles, Lock, CreditCard, Loader2, CheckCircle2, X, QrCode } from "lucide-react";
import { QrisModal, fmtIDR } from "../components/QrisModal";

export default function Newsletter() {
  const user = getUser();
  const [params, setParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [qris, setQris] = useState(null);
  const [qrisPayment, setQrisPayment] = useState(() => (params.get("order_id") ? { order_id: params.get("order_id"), amount: 0, qr_url: null, redirect_url: null } : null));
  const [status, setStatus] = useState(null);
  const [digests, setDigests] = useState([]);
  const [openDigest, setOpenDigest] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/newsletter/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/newsletter/plan").then((r) => setPlan(r.data)).catch(() => {});
    api.get("/payments/qris/config").then((r) => {
      setQris(r.data);
      setQrisPayment((p) => (p && !p.amount ? { ...p, amount: r.data.price_idr } : p));
    }).catch(() => {});
    api.get("/newsletter/digests").then((r) => setDigests(r.data)).catch(() => {});
    if (user) loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payQris = async () => {
    if (!user) {
      toast.info("Masuk atau daftar dulu untuk berlangganan");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/payments/qris/checkout", { origin_url: window.location.origin });
      if (r.data.redirect_url) window.open(r.data.redirect_url, "_blank", "noopener");
      setQrisPayment(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat QRIS");
    }
    setBusy(false);
  };

  const closeQris = () => {
    setQrisPayment(null);
    if (params.get("order_id")) setParams({});
  };

  const subscribe = async () => {
    if (!user) {
      toast.info("Masuk atau daftar dulu untuk berlangganan");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memulai pembayaran");
      setBusy(false);
    }
  };

  const readDigest = async (id) => {
    try {
      const r = await api.get(`/newsletter/digests/${id}`);
      setOpenDigest(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Khusus pelanggan newsletter");
    }
  };

  const subscribed = status?.subscribed || user?.role === "admin";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
      <div className="mb-10 text-left">
        <div className="chip chip-magenta mb-3"><Mail className="w-3 h-3" /> Newsletter Premium</div>
        <h1 className="font-heading text-4xl md:text-5xl font-black">Digest Mingguan <span className="gradient-text">AI</span></h1>
        <p className="text-slate-400 mt-3 max-w-2xl">Rangkuman berita AI &amp; Robotics terbaik setiap minggu, dikurasi dan dianalisis oleh AI. Eksklusif untuk pelanggan.</p>
      </div>

      {!subscribed && plan && (
        <div className="card p-8 mb-10 relative overflow-hidden" data-testid="subscribe-card">
          <div className="absolute -inset-1 bg-gradient-to-br from-cyan1/10 to-magenta1/10 pointer-events-none" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold">{plan.plan_name}</h2>
              <div className="font-heading text-4xl font-black mt-2">
                ${plan.price_usd}<span className="text-base text-slate-500 font-normal">/{plan.plan_period}</span>
              </div>
              <ul className="mt-4 space-y-1 text-sm text-slate-400">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Akses penuh arsip digest mingguan</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Analisis AI dari berita terpilih</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> 1 akun = 1 langganan, aman &amp; terverifikasi</li>
              </ul>
              <p className="text-xs text-slate-600 font-mono mt-3">
                Kartu via Stripe · Visa / Mastercard{plan.crypto_enabled ? " / Crypto (USDC)" : ""}
                {qris?.enabled && <> · QRIS {fmtIDR(qris.price_idr)} via Midtrans</>}
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full md:w-auto">
              {qris?.enabled && (
                <button onClick={payQris} disabled={busy || !plan.payments_enabled} className="btn-primary text-base !px-8 !py-3 justify-center" data-testid="subscribe-qris-btn">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <QrCode className="w-5 h-5" />}
                  Bayar QRIS · {fmtIDR(qris.price_idr)}
                </button>
              )}
              <button onClick={subscribe} disabled={busy || !plan.payments_enabled} className={`${qris?.enabled ? "btn-ghost" : "btn-primary"} text-base !px-8 !py-3 justify-center`} data-testid="subscribe-btn">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                {plan.payments_enabled ? (qris?.enabled ? `Kartu · $${plan.price_usd}` : "Berlangganan Sekarang") : "Pembayaran Nonaktif Sementara"}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrisPayment && (
        <QrisModal payment={qrisPayment} onClose={closeQris} onPaid={loadStatus} />
      )}

      {subscribed && (
        <div className="card p-5 mb-10 flex items-center gap-3 border-green-500/30" data-testid="subscribed-badge">
          <CheckCircle2 className="w-6 h-6 text-green-400" />
          <div>
            <div className="font-heading font-bold">Anda pelanggan aktif</div>
            <div className="text-xs text-slate-500 font-mono">Akses penuh ke semua digest mingguan</div>
          </div>
        </div>
      )}

      <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-cyan1" /> Arsip Digest
      </h2>
      {digests.length === 0 ? (
        <p className="text-slate-600 text-sm" data-testid="digests-empty">Digest pertama akan terbit minggu ini. Nantikan!</p>
      ) : (
        <div className="space-y-4">
          {digests.map((d) => (
            <button key={d.id} onClick={() => readDigest(d.id)} className="card p-5 w-full text-left flex items-center gap-4 hover:border-cyan1/40 transition" data-testid={`digest-${d.id}`}>
              <div className="chip chip-cyan shrink-0">{d.week}</div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold truncate">{d.title}</h3>
                <div className="text-xs text-slate-500 font-mono mt-1">{d.article_count} berita · {new Date(d.created_at).toLocaleDateString("id-ID")}</div>
              </div>
              {!subscribed && <Lock className="w-4 h-4 text-slate-600 shrink-0" />}
            </button>
          ))}
        </div>
      )}

      {!user && (
        <p className="text-slate-500 text-sm mt-8">
          <Link to="/login" className="text-cyan1 hover:underline">Masuk / daftar</Link> untuk berlangganan.
        </p>
      )}

      {openDigest && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpenDigest(null)}>
          <div className="card max-w-3xl w-full p-8 my-8 relative" onClick={(e) => e.stopPropagation()} data-testid="digest-modal">
            <button onClick={() => setOpenDigest(null)} className="absolute top-4 right-4 text-slate-500 hover:text-white" data-testid="digest-close">
              <X className="w-5 h-5" />
            </button>
            <div className="chip chip-magenta mb-4">{openDigest.week}</div>
            <div className="prose-neon max-w-none">
              <ReactMarkdown>{openDigest.content}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
